import React, { useState, useRef } from "react";
import { createRegistration, updateRegistration, getRegistrationByPersonAndEvent } from "../../../Services/Registration";
import { IRegistration } from "../../../interfaces/models/IRegistration";
import { AttendeesStatus, AttendeeType, PaymentMethod } from "../../../Enums/RegistrationEnum";
import { getPersonByLegacyId } from "../../../Services/Person";
import { getFirmByIdOrLegacyId } from "../../../Services/Firm";
import { EVENT_DATA_REGISTRY } from "./data";
import { LegacyRegistrationRecord } from "./data/WSGIBAAnnualNetworkingLuncheon2025";


/**
 * Generic component to insert legacy registration data
 * Allows selecting which event data to insert
 */
const LegacyRegistrationInserter: React.FC = () => {
  const [selectedEventId, setSelectedEventId] = useState<string>(EVENT_DATA_REGISTRY[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdRegistrations, setCreatedRegistrations] = useState<IRegistration[]>([]);
  const [updatedRegistrations, setUpdatedRegistrations] = useState<IRegistration[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [updateMode, setUpdateMode] = useState(true); // Toggle between insert and update mode

  const eventIdRef = useRef<HTMLInputElement>(null);

  // Get selected event data
  const selectedEvent = EVENT_DATA_REGISTRY.find(e => e.id === selectedEventId);
  const legacyRecords = selectedEvent?.records || [];

  /**
   * Converts legacy date string to Unix timestamp (seconds)
   * Format: "2025-07-08 05:19:44.470"
   */
  const convertDateToTimestamp = (dateString: string): number => {
    try {
      const date = new Date(dateString);
      return Math.floor(date.getTime() / 1000);
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return Math.floor(Date.now() / 1000);
    }
  };

  /**
   * Maps legacy payment type to PaymentMethod enum
   */
  const mapPaymentMethod = (legacyPayType: string): PaymentMethod => {
    const normalized = legacyPayType.trim().toLowerCase();
    if (normalized.includes('wire')) {
      return 'wireTransfer';
    }
    if (normalized.includes('credit') || normalized.includes('card') || normalized === 'cc') {
      return 'creditCard';
    }
    if (normalized === 'check' || normalized === 'cheque') {
      return 'check';
    }
    if (normalized === 'prepaid' || normalized === 'pre-paid') {
      return 'prepaid';
    }
    if (normalized === 'nocharge' || normalized === 'sponsor') {
      return 'creditCard'; // Default for no-charge/sponsor registrations
    }
    return 'wireTransfer'; // Default
  };

  /**
   * Creates registration from legacy data record
   */
  const createLegacyRegistration = async (
    legacyRecord: LegacyRegistrationRecord,
    eventId: string
  ): Promise<IRegistration> => {
    // Get person by legacy ID
    const person = await getPersonByLegacyId(legacyRecord.regcontactid);
    if (!person || !person.id) {
      throw new Error(`Person not found with legacy ID: ${legacyRecord.regcontactid} (${legacyRecord.regname})`);
    }

    // Get firm by legacy ID (or use person's firm if available)
    let firmId: string | undefined = undefined;
    if (person.firm) {
      // Person already has a firm ID, use it
      firmId = person.firm;
    } else if (legacyRecord.regcompid) {
      // Try to get firm by legacy ID
      const firm = await getFirmByIdOrLegacyId(legacyRecord.regcompid);
      if (firm && firm.id) {
        firmId = firm.id;
      }
    }

    // Map payment method
    const paymentMethod = mapPaymentMethod(legacyRecord.regpaytype);

    // Map payment status (1 = completed, 0 = pending)
    // For update mode, we'll determine this based on amount paid
    const paymentStatus: 'pending' | 'completed' | 'partial' = legacyRecord.regpaid === 1 ? 'completed' : 'pending';

    // Convert registration date
    const registrationDate = convertDateToTimestamp(legacyRecord.regcreated);

    // Use badge name or fallback to person name
    const badgeName = legacyRecord.rbadge?.trim() || legacyRecord.regname;

    // Get totalAmount from legacy record if provided (for manual insertion)
    const totalAmount = (legacyRecord as any).totalAmount;

    const registration: IRegistration = {
      eventId,
      personId: person.id,
      attendeeType: 'regular', // All legacy registrations are regular
      firmId,
      badgeName,
      guestName: legacyRecord.rguest1?.trim() || undefined,
      specialRequests: legacyRecord.rdietary?.trim() || legacyRecord.regcomments?.trim() || undefined,
      paymentMethod,
      paymentStatus,
      registrationDate,
      prepaid: paymentMethod === 'prepaid',
      createdDate: Math.floor(Date.now() / 1000),
      isDeleted: false,
      totalAmount: totalAmount !== undefined && totalAmount !== null ? totalAmount : undefined, // Store totalAmount if provided
    };

    return registration;
  };

  /**
   * Determines payment status based on amount paid and total amount
   */
  const determinePaymentStatus = (
    amountPaid: number,
    totalAmount: number
  ): 'pending' | 'completed' | 'partial' => {
    if (amountPaid <= 0) {
      return 'pending';
    } else if (amountPaid >= totalAmount) {
      return 'completed';
    } else {
      return 'partial';
    }
  };

  /**
   * Updates an existing registration with payment status and amount
   */
  const updateLegacyRegistration = async (
    legacyRecord: LegacyRegistrationRecord | any,
    eventId: string
  ): Promise<IRegistration | null> => {
    // Get person by legacy ID
    const person = await getPersonByLegacyId(legacyRecord.regcontactid);
    if (!person || !person.id) {
      throw new Error(`Person not found with legacy ID: ${legacyRecord.regcontactid} (${legacyRecord.regname})`);
    }

    // Find existing registration
    const existingRegistration = await getRegistrationByPersonAndEvent(person.id, eventId);
    if (!existingRegistration || !existingRegistration.id) {
      throw new Error(`Registration not found for person ${legacyRecord.regname} (${legacyRecord.regcontactid}) and event ${eventId}`);
    }

    // Get totalAmount from legacy record if provided (for manual insertion), otherwise use existing
    const totalAmountFromLegacy = (legacyRecord as any).totalAmount;
    const totalAmount = totalAmountFromLegacy !== undefined && totalAmountFromLegacy !== null
      ? totalAmountFromLegacy
      : (existingRegistration.totalAmount ?? 0);

    // Check if this is an update record with explicit amountPaid
    const isUpdateRecord = 'amountPaid' in legacyRecord;

    let amountPaid: number;
    let paymentStatus: 'pending' | 'completed' | 'partial';

    if (isUpdateRecord) {
      // Use explicit amountPaid from update record
      amountPaid = legacyRecord.amountPaid;
    } else {
      // Determine amount paid based on regpaid status
      // regpaid: 1 = paid (full amount), 0 = unpaid (0 amount)
      if (legacyRecord.regpaid === 1) {
        amountPaid = totalAmount; // Full payment
      } else {
        amountPaid = 0; // Unpaid
      }
    }

    // Determine payment status based on regpaid + amountPaid
    // If regpaid = 1 → completed (full payment)
    // If regpaid = 0 and amountPaid = 0 → pending (unpaid)
    // If regpaid = 0 but amountPaid > 0 → partial (partial payment)
    if (legacyRecord.regpaid === 1) {
      paymentStatus = 'completed';
    } else if (amountPaid === 0) {
      paymentStatus = 'pending';
    } else {
      paymentStatus = 'partial';
    }

    // Create update object - preserve all existing fields, only update payment-related fields
    const updatedRegistration: IRegistration = {
      ...existingRegistration,
      paymentStatus,
      amountPaid,
      totalAmount: totalAmountFromLegacy !== undefined && totalAmountFromLegacy !== null
        ? totalAmountFromLegacy
        : (existingRegistration.totalAmount ?? totalAmount),
    };

    // Apply cancelled date and status from update record when present
    const legacyUpdate = legacyRecord as { dateCanceled?: number; status?: AttendeesStatus; attendeeType?: AttendeeType };
    if (legacyUpdate.dateCanceled != null) {
      const raw = legacyUpdate.dateCanceled;
      updatedRegistration.cancelRegistrationDate = raw >= 1e12 ? Math.floor(raw / 1000) : raw;
    }
    if (legacyUpdate.status != null) {
      updatedRegistration.status = legacyUpdate.status;
    }
    if (legacyUpdate.attendeeType != null) {
      updatedRegistration.attendeeType = legacyUpdate.attendeeType;
    }

    // Update the registration
    const response = await updateRegistration(updatedRegistration);
    return response;
  };

  /**
   * Handles the insertion or update of all legacy registrations
   */
  const handleInsertAllLegacy = async () => {
    if (!selectedEvent || legacyRecords.length === 0) {
      setError('Please select an event with registration data');
      return;
    }

    setLoading(true);
    setSuccess(false);
    setError(null);
    setCreatedRegistrations([]);
    setUpdatedRegistrations([]);
    setProgress({ current: 0, total: legacyRecords.length });

    try {
      const eventIdInput = eventIdRef.current?.value?.trim();
      if (!eventIdInput) {
        setError('Please enter Event IDs mapping (JSON format: {"Event Name": "event-uuid"})');
        setLoading(false);
        return;
      }

      // Parse event mapping
      let eventMapping: Record<string, string> | null = null;
      let singleEventId: string | null = null;

      // Check if input is a single UUID first
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const trimmedInput = eventIdInput.trim();

      if (uuidRegex.test(trimmedInput)) {
        // Single UUID provided - use for all records
        singleEventId = trimmedInput;
      } else {
        // Try to parse as JSON mapping
        try {
          const parsed = JSON.parse(eventIdInput);
          if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            eventMapping = parsed;
          } else {
            setError('Event ID must be a valid UUID format or JSON object mapping');
            setLoading(false);
            return;
          }
        } catch {
          setError('Event ID must be a valid UUID format or JSON mapping');
          setLoading(false);
          return;
        }
      }

      const results: IRegistration[] = [];
      const errors: Array<{ recid: number; error: string }> = [];
      const skipped: Array<{ recid: number; reason: string }> = [];

      // Process each record
      for (let i = 0;i < legacyRecords.length;i++) {
        const record = legacyRecords[i];
        setProgress({ current: i + 1, total: legacyRecords.length });

        try {
          // If single UUID provided, use it for all records; otherwise use mapping
          const eventId = singleEventId || (eventMapping && eventMapping[record.regevent]);
          if (!eventId) {
            errors.push({
              recid: record.recid,
              error: `Event ID not found for event: "${record.regevent}"`
            });
            continue;
          }

          if (updateMode) {
            // Update existing registration
            const response = await updateLegacyRegistration(record, eventId);
            if (response) {
              results.push(response);
            }
          } else {
            // Create new registration - but check if it already exists first
            const registration = await createLegacyRegistration(record, eventId);

            // Check if registration already exists for this person + event
            try {
              const existingRegistration = await getRegistrationByPersonAndEvent(registration.personId, eventId);
              if (existingRegistration && existingRegistration.id) {
                // Registration already exists - skip it
                skipped.push({
                  recid: record.recid,
                  reason: `Registration already exists for ${record.regname}`
                });
                continue;
              }
            } catch (checkError) {
              // If check fails, proceed with creation (might be a new registration)
              console.warn(`Could not check for existing registration for ${record.regname}:`, checkError);
            }

            // Create the registration
            const response = await createRegistration(registration);
            results.push(response);
          }
        } catch (err: any) {
          console.error(`Error ${updateMode ? 'updating' : 'creating'} registration for recid ${record.recid}:`, err);
          errors.push({
            recid: record.recid,
            error: err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              'Unknown error'
          });
        }
      }

      if (updateMode) {
        setUpdatedRegistrations(results);
      } else {
        setCreatedRegistrations(results);
      }

      if (results.length > 0) {
        setSuccess(true);
      }

      // Build error/skip message
      const messages: string[] = [];
      if (errors.length > 0) {
        messages.push(`${errors.length} registration(s) failed`);
        console.error('Failed registrations:', errors);
      }
      if (skipped.length > 0) {
        messages.push(`${skipped.length} registration(s) skipped (already exist)`);
        console.log('Skipped registrations:', skipped);
      }
      if (messages.length > 0) {
        setError(messages.join('. ') + '. Check console for details.');
      }

      // Reset success message after 15 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 15000);
    } catch (err: any) {
      console.error('Error processing legacy registrations:', err);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to process registrations. Please check the console for details.'
      );
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const uniqueEvents = selectedEvent ? [...new Set(legacyRecords.map(r => r.regevent))] : [];

  return (
    <div className="w-6/12 mx-auto p-5 border border-gray-300 shadow-sm shadow-black my-12 rounded-lg">
      <div className="w-full mx-auto">
        <h1 className="text-center text-lg my-3 font-semibold">Legacy Registration {updateMode ? 'Updater' : 'Inserter'}</h1>

        <div className="w-full mb-4 flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={updateMode}
              onChange={(e) => setUpdateMode(e.target.checked)}
              disabled={loading}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium text-gray-700">
              Update Mode (modify existing registrations)
            </span>
          </label>
        </div>

        {updateMode && (
          <div className="w-full mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              <strong>Update Mode:</strong> This will find existing registrations and update their payment status and amount based on the legacy data.
              <br />
              <strong>Payment Status:</strong> regpaid = 1 → "completed" (full payment), regpaid = 0 → "pending" (unpaid)
            </p>
          </div>
        )}

        <div className="w-full mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Event <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            {EVENT_DATA_REGISTRY.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name} ({event.records.length} records)
              </option>
            ))}
          </select>
        </div>

        {selectedEvent && (
          <>
            <p className="text-center text-sm text-gray-600 mb-4">
              This will {updateMode ? 'update' : 'insert'} {legacyRecords.length} legacy registration records for <strong>{selectedEvent.name}</strong>.
            </p>

            <div className="w-full mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event ID Mapping (JSON) <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={eventIdRef as any}
                placeholder='Enter a single event UUID (e.g., "852a6a1b-1f3a-4068-bbfb-e8ca00f6bc80") or JSON mapping: {"Event Name": "event-uuid"}'
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                <strong>Recommended:</strong> Enter a single UUID to use for all {legacyRecords.length} records in this file (ignores event name variations).
                <br />
                <strong>Alternative:</strong> Enter JSON mapping of event names to UUIDs if you need different UUIDs per event name.
                <br />
                Unique event names found: {uniqueEvents.join(', ')}
              </p>
            </div>

            {loading && progress.total > 0 && (
              <div className="w-full mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-700">
                    Processing: {progress.current} / {progress.total} registrations
                  </p>
                  <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            <div className="w-full flex justify-center align-middle mb-4">
              <button
                type="button"
                onClick={handleInsertAllLegacy}
                disabled={loading || legacyRecords.length === 0}
                className={`w-fit border border-black rounded-md px-5 py-2 my-2 mx-auto ${loading || legacyRecords.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
              >
                {loading ? `Processing... (${progress.current}/${progress.total})` : `${updateMode ? 'Update' : 'Insert'} All ${legacyRecords.length} Registrations`}
              </button>
            </div>

            {error && (
              <div className="w-full mx-auto flex align-middle justify-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {success && (updateMode ? updatedRegistrations.length > 0 : createdRegistrations.length > 0) && (
              <div className="w-full mx-auto bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                <p className="text-sm font-semibold mb-2">
                  Successfully {updateMode ? 'Updated' : 'Created'} {updateMode ? updatedRegistrations.length : createdRegistrations.length} Registration(s)!
                </p>
                <div className="text-xs mt-2 space-y-1 max-h-60 overflow-y-auto">
                  <p><strong>Total Processed:</strong> {legacyRecords.length}</p>
                  <p><strong>Successfully {updateMode ? 'Updated' : 'Created'}:</strong> {updateMode ? updatedRegistrations.length : createdRegistrations.length}</p>
                  <p><strong>Failed:</strong> {legacyRecords.length - (updateMode ? updatedRegistrations.length : createdRegistrations.length)}</p>
                  {(updateMode ? updatedRegistrations : createdRegistrations).length <= 10 && (
                    <div className="mt-2">
                      <p className="font-semibold">{updateMode ? 'Updated' : 'Created'} Registrations:</p>
                      {(updateMode ? updatedRegistrations : createdRegistrations).map((reg, idx) => (
                        <p key={idx} className="ml-2">
                          • {reg.badgeName} - Status: {reg.paymentStatus} - Amount: ${reg.totalAmount ?? 0} (ID: {reg.id?.substring(0, 8)}...)
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="w-full mt-4 p-3 bg-gray-50 rounded border border-gray-200">
              <p className="text-xs text-gray-600 font-semibold mb-2">
                Legacy Registration Data Summary:
              </p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                <li><strong>Total Records:</strong> {legacyRecords.length}</li>
                <li><strong>Unique Events:</strong> {uniqueEvents.length}</li>
                <li><strong>Events:</strong> {uniqueEvents.join(', ')}</li>
                <li><strong>Payment Methods:</strong> {[...new Set(legacyRecords.map(r => r.regpaytype.trim()))].join(', ')}</li>
                <li><strong>Payment Status:</strong> {legacyRecords.filter(r => r.regpaid === 1).length} completed, {legacyRecords.filter(r => r.regpaid === 0).length} pending</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LegacyRegistrationInserter;

